import { API_URL } from '../../config/api';

/**
 * Registrar — transcrição em tempo real: protocolo com o relay do servidor (WebSocket nosso → AI Gateway,
 * openai/gpt-realtime-whisper). Áudio PCM 16 bits mono 24 kHz em BINARY; controle e resultados em JSON.
 */
export const REALTIME_SAMPLE_RATE = 24000;
export const REALTIME_PROTOCOL = 'mends-register-stt.v1';

/** Mensagens do servidor para a tela. */
export type RealtimeServerMessage =
  | { type: 'ready'; model?: string; sampleRate?: number }
  | { type: 'partial'; text: string; final: string }
  | { type: 'limit'; seconds: number }
  | { type: 'reconnecting' }
  | { type: 'done'; text: string; durationSeconds?: number; stopToFinalMs?: number | null; reconnects?: number }
  | { type: 'error'; code: string; message: string; text?: string };

export function parseRealtimeMessage(raw: unknown): RealtimeServerMessage | null {
  if (typeof raw !== 'string') return null;
  try {
    const m = JSON.parse(raw);
    if (!m || typeof m !== 'object' || typeof m.type !== 'string') return null;
    switch (m.type) {
      case 'ready':
      case 'reconnecting':
        return m;
      case 'partial':
        return typeof m.text === 'string' ? { type: 'partial', text: m.text, final: typeof m.final === 'string' ? m.final : '' } : null;
      case 'limit':
        return { type: 'limit', seconds: Number(m.seconds) || 0 };
      case 'done':
        return typeof m.text === 'string' ? m : null;
      case 'error':
        return { type: 'error', code: String(m.code || 'unknown'), message: String(m.message || ''), text: typeof m.text === 'string' ? m.text : undefined };
      default:
        return null;
    }
  } catch {
    return null;
  }
}

/** wss://…/api/mends-vision/register-transcription/stream?sectorId&requestId (mesma origem da API). */
export function realtimeStreamUrl(sectorId: number, requestId: string, apiUrl: string = API_URL): string {
  const base = apiUrl.replace(/\/$/, '').replace(/^http/i, 'ws');
  const q = new URLSearchParams({ sectorId: String(sectorId), requestId });
  return `${base}/mends-vision/register-transcription/stream?${q.toString()}`;
}

/** JWT no subprotocolo (o WebSocket do navegador não manda header; token na URL vai para log). */
export function realtimeProtocols(token: string): string[] {
  return [REALTIME_PROTOCOL, `mends-auth.${token}`];
}

/** Texto amigável por código de erro do relay. */
export function realtimeErrorMessage(code: string): string {
  switch (code) {
    case 'not_configured':
      return 'Transcrição em tempo real indisponível neste servidor.';
    case 'gateway_unavailable':
    case 'connection_lost':
      return 'A conexão da transcrição caiu.';
    case 'timeout':
      return 'O texto final não chegou a tempo.';
    case 'rate_limited':
      return 'Muitas transcrições ao mesmo tempo. Tente de novo em instantes.';
    case 'gateway_auth':
      return 'O provedor de transcrição recusou o acesso (crédito ou chave).';
    case 'session_limit':
      return 'A gravação passou do tempo máximo.';
    default:
      return 'A transcrição em tempo real falhou.';
  }
}

/**
 * Reamostragem linear Float32 (taxa do microfone) → Int16 24 kHz, contínua entre blocos. Código em string porque roda
 * dentro do AudioWorklet (módulo separado); os testes avaliam a mesma fonte.
 */
export const RESAMPLER_SOURCE = `
function createResampler(inRate, outRate) {
  var ratio = inRate / outRate;
  var t = 0;
  var prev = 0;
  return function (ch) {
    var len = ch.length;
    var out = [];
    if (!len) return new Int16Array(0);
    while (t < len) {
      var i0 = Math.floor(t);
      var f = t - i0;
      var a = i0 === 0 ? prev : ch[i0 - 1];
      var b = ch[i0];
      var s = a + (b - a) * f;
      if (s > 1) s = 1; else if (s < -1) s = -1;
      out.push(s < 0 ? Math.round(s * 32768) : Math.round(s * 32767));
      t += ratio;
    }
    t -= len;
    prev = ch[len - 1];
    return Int16Array.from(out);
  };
}
`;

/** Worklet: junta ~100 ms (2400 amostras a 24 kHz) e manda para a thread principal. */
export const WORKLET_SOURCE = `${RESAMPLER_SOURCE}
class MendsPcmCapture extends AudioWorkletProcessor {
  constructor() {
    super();
    this.resample = createResampler(sampleRate, ${REALTIME_SAMPLE_RATE});
    this.buf = new Int16Array(2400);
    this.n = 0;
    this.port.onmessage = (e) => {
      if (e.data === 'flush' && this.n > 0) {
        this.port.postMessage(this.buf.slice(0, this.n).buffer);
        this.n = 0;
      }
    };
  }
  process(inputs) {
    var ch = inputs[0] && inputs[0][0];
    if (!ch) return true;
    var pcm = this.resample(ch);
    for (var i = 0; i < pcm.length; i++) {
      this.buf[this.n++] = pcm[i];
      if (this.n === this.buf.length) {
        this.port.postMessage(this.buf.slice(0).buffer);
        this.n = 0;
      }
    }
    return true;
  }
}
registerProcessor('mends-pcm-capture', MendsPcmCapture);
`;

/** Navegador tem o necessário para o tempo real (senão a tela grava e oferece o modo gravado). */
export function realtimeSupported(): boolean {
  return typeof window !== 'undefined'
    && typeof WebSocket !== 'undefined'
    && typeof AudioContext !== 'undefined'
    && typeof AudioWorkletNode !== 'undefined'
    && !!navigator.mediaDevices?.getUserMedia;
}
